// client.ts — thin fetch wrapper for the Animus panel.
//
// Why a wrapper:
//  - Inject the CSRF header on mutating requests (the SPA holds it in
//    memory after /me).
//  - Detect 401 globally and let the caller redirect to /login.
//
// The CSRF token is read from the sa_csrf cookie (not HttpOnly) on
// initial load, and from the response of /api/v1/auth/login after the
// user logs in.

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

let csrfToken: string | null = null;

export function setCsrf(token: string | null) {
  csrfToken = token;
}

export function getCsrf(): string | null {
  if (csrfToken) return csrfToken;
  // Fallback: read from cookie if the SPA was reloaded mid-session.
  const m = document.cookie.match(/(?:^|; )sa_csrf=([^;]+)/);
  if (m) csrfToken = decodeURIComponent(m[1]);
  return csrfToken;
}

interface RequestOpts {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  // Skip CSRF (for unauthenticated endpoints like /auth/login).
  skipCsrf?: boolean;
}

export async function api<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const method = opts.method ?? "GET";
  const headers: Record<string, string> = { Accept: "application/json" };
  let body: BodyInit | undefined;
  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(opts.body);
  }
  if (method !== "GET" && method !== "HEAD" && !opts.skipCsrf) {
    const tok = getCsrf();
    if (tok) headers["X-CSRF-Token"] = tok;
  }
  const res = await fetch(path, {
    method,
    headers,
    body,
    credentials: "same-origin",
    signal: opts.signal,
  });
  const text = await res.text();
  let parsed: unknown = text;
  if (text && res.headers.get("content-type")?.includes("application/json")) {
    try {
      parsed = JSON.parse(text);
    } catch {
      // leave as text
    }
  }
  if (!res.ok) {
    throw new ApiError(res.status, `${method} ${path} -> ${res.status}`, parsed);
  }
  return parsed as T;
}
